import { h } from "@/lib/dom";
import { openModal, field, textInput, select, button, toast } from "@/components/ui";
import { navigate } from "@/app/router";
import { topicsService, pbErrorMessage } from "@/services";
import { EXAM_TYPE_OPTIONS } from "./examFields";
import { examTermField } from "./examTermField";
import type { ExamType, Topic } from "@/types/models";

/**
 * Einstellungen pro Thema (Zahnrad oben rechts): Titel plus der eine
 * Prüfungstermin des Themas. Archivieren/Löschen stehen hier bewusst nicht zur
 * Verfügung – dafür gibt es den Stift auf der Startseite.
 */
export async function openTopicSettings(opts: {
  topic: Topic;
  onChanged: () => void;
  /** Zeigt unten den Sprung zur Themenseite – für Aufrufe von woanders. */
  showTopicLink?: boolean;
}): Promise<void> {
  const { topic } = opts;

  const titleInput = textInput({ value: topic.title, required: true });
  const errorBox = h("div", { class: "field-error" });
  const saveBtn = button({ label: "Speichern", variant: "primary", onClick: () => void save() });

  const termField = examTermField({
    subjectId: topic.subject,
    value: topic.examDate
      ? {
          date: topic.examDate.slice(0, 10),
          periodFrom: topic.examPeriodFrom,
          periodTo: topic.examPeriodTo,
        }
      : undefined,
  });
  const typeSelect = select({
    options: EXAM_TYPE_OPTIONS,
    value: topic.examType || "klassenarbeit",
  });

  async function save(): Promise<void> {
    const title = titleInput.value.trim();
    if (!title) {
      errorBox.textContent = "Bitte einen Titel eingeben.";
      return;
    }
    const term = termField.getValue();
    saveBtn.disabled = true;
    errorBox.textContent = "";
    try {
      await topicsService.update(topic.id, { title });
      await topicsService.setExam(topic.id, {
        date: term.date,
        examType: typeSelect.value as ExamType,
        periodFrom: term.periodFrom,
        periodTo: term.periodTo,
      });
      toast("Einstellungen gespeichert.", "success");
      handle.close();
      opts.onChanged();
    } catch (err) {
      errorBox.textContent = pbErrorMessage(err);
      saveBtn.disabled = false;
    }
  }

  const handle = openModal({
    title: "Thema-Einstellungen",
    body: h(
      "div",
      null,
      field("Titel", titleInput),
      field("Prüfungstermin", termField.el),
      field("Prüfungsform", typeSelect),
      errorBox
    ),
    footer: h(
      "div",
      { class: "row" },
      // Von der Themenseite aus wäre der Sprung sinnlos – nur von woanders.
      opts.showTopicLink
        ? button({
            icon: "link",
            variant: "ghost",
            title: "Zur Themenseite",
            onClick: () => {
              handle.close();
              navigate(`/topic/${topic.id}`);
            },
          })
        : null,
      h("span", { class: "spacer" }),
      button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
      saveBtn
    ),
  });
}
